import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from ".";

const STORAGE_KEY_MENU_FAVORITES = "cpln_menu_favorites";
const STORAGE_KEY_MENU_EXPANDED = "cpln_menu_expanded_section";

export interface MenuItem {
  id: string;
  label: string;
  url: string;
  icon?: React.ReactNode;
  category: string;
  subCategory?: string;
  customIsActive?: (pathname: string) => boolean;
  canFavorite?: boolean;
}

interface MenuState {
  favoriteIds: string[];
  expandedSection: string | null;
  isEditingFavorites: boolean;
}

function loadFavoritesFromStorage(): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_MENU_FAVORITES);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function loadExpandedSectionFromStorage(): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_MENU_EXPANDED);
    // Default to "resources" if nothing is stored
    return stored || "resources";
  } catch {
    return "resources";
  }
}

const initialState: MenuState = {
  favoriteIds: loadFavoritesFromStorage(),
  expandedSection: loadExpandedSectionFromStorage(),
  isEditingFavorites: false,
};

export const menuSlice = createSlice({
  name: "menu",
  initialState,
  reducers: {
    toggleFavorite: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const index = state.favoriteIds.indexOf(id);

      if (index >= 0) {
        // Remove from favorites
        state.favoriteIds.splice(index, 1);
      } else {
        // Add to favorites
        state.favoriteIds.push(id);
      }

      // Persist to localStorage
      localStorage.setItem(STORAGE_KEY_MENU_FAVORITES, JSON.stringify(state.favoriteIds));
    },

    reorderFavorites: (state, action: PayloadAction<{ oldIndex: number; newIndex: number }>) => {
      const { oldIndex, newIndex } = action.payload;
      const [removed] = state.favoriteIds.splice(oldIndex, 1);
      state.favoriteIds.splice(newIndex, 0, removed);

      // Persist to localStorage
      localStorage.setItem(STORAGE_KEY_MENU_FAVORITES, JSON.stringify(state.favoriteIds));
    },

    setExpandedSection: (state, action: PayloadAction<string | null>) => {
      state.expandedSection = action.payload;

      // Persist to localStorage
      if (action.payload) {
        localStorage.setItem(STORAGE_KEY_MENU_EXPANDED, action.payload);
      } else {
        localStorage.removeItem(STORAGE_KEY_MENU_EXPANDED);
      }
    },

    toggleSection: (state, action: PayloadAction<string>) => {
      const section = action.payload;

      if (state.expandedSection === section) {
        state.expandedSection = null;
        localStorage.removeItem(STORAGE_KEY_MENU_EXPANDED);
      } else {
        state.expandedSection = section;
        localStorage.setItem(STORAGE_KEY_MENU_EXPANDED, section);
      }
    },

    setEditingFavorites: (state, action: PayloadAction<boolean>) => {
      state.isEditingFavorites = action.payload;
    },

    toggleEditingFavorites: (state) => {
      state.isEditingFavorites = !state.isEditingFavorites;
    },
  },
});

export const { toggleFavorite, reorderFavorites, setExpandedSection, toggleSection, setEditingFavorites, toggleEditingFavorites } = menuSlice.actions;

export const menuReducer = menuSlice.reducer;

// Selectors
export const selectFavoriteIds = (state: RootState) => state.menu.favoriteIds;
export const selectExpandedSection = (state: RootState) => state.menu.expandedSection;
export const selectIsEditingFavorites = (state: RootState) => state.menu.isEditingFavorites;
export const selectIsFavorite = (id: string) => (state: RootState) =>
  state.menu.favoriteIds.includes(id);

export const MENU_EXPAND_EVENT = "menu-expand-section";

export function expandMenuSection(section: string | null) {
  window.dispatchEvent(new CustomEvent(MENU_EXPAND_EVENT, { detail: section }));
}
